<template>
  <div class="qaqc-unified-dashboard">
    <!-- Summary cards row -->
    <div class="qaqc-overview-cards-row">
      <SummaryCards :stats="summaryStats" />
    </div>

    <!-- Middle row: QC Status Overview chart + QC Status panel -->
    <div class="qaqc-overview-middle-row">
      <div class="qaqc-overview-chart-col">
        <QCStatusOverviewChart :data="qcStatusOverview" />
      </div>
      <div class="qaqc-overview-status-col">
        <QCStatusPanel
          :panel="qcStatusPanelNormalized"
          :sample-ids="sampleIds"
          :selected-sample-ids="selectedSampleIds"
          @update:selected-sample-ids="$emit('update:selectedSampleIds', $event)"
        />
      </div>
    </div>

    <!-- Tabs: Oil CCE Tests | Gas CCE Tests | DL Tests -->
    <div class="qaqc-overview-tabs-wrap">
      <QAQCTestTabs
        :model-value="activeTestTab"
        @update:model-value="$emit('update:activeTestTab', $event)"
      />
    </div>

    <!-- Tab content: CCE or DL view -->
    <CCETestView
      v-if="activeTestTab === 'oilCce' || activeTestTab === 'gasCce'"
      :scorecard-rows="scorecardRows"
      :scorecard-summary="scorecardSummary"
      :validation-matrix-rows="validationMatrixRows"
      :validation-matrix-title="validationMatrixTitle"
      :validation-matrix-description="validationMatrixDescription"
      :dataset-label="activeTestTab === 'oilCce' ? 'CCE Dataset (Oil)' : 'CCE Dataset (Gas)'"
    />
    <DLTestView
      v-else-if="activeTestTab === 'dlTests'"
      :scorecard-rows="scorecardRows"
      :scorecard-summary="scorecardSummary"
      :validation-matrix-rows="validationMatrixRows"
      :validation-matrix-title="validationMatrixTitle"
      :validation-matrix-description="validationMatrixDescription"
      :consistency-cross-test-rows="consistencyCrossTestRows"
      :consistency-ordering-rows="consistencyOrderingRows"
      :equilibrium-plot-rows="equilibriumPlotRows"
      :distribution-plot-options="distributionPlotOptions"
      :selected-plot-id="selectedPlotId"
      :distribution-data="distributionData"
      :dl-material-balance-plot-options="dlMaterialBalancePlotOptions"
      :selected-material-balance-plot-id="selectedMaterialBalancePlotId"
      :dl-material-balance-distribution-data="dlMaterialBalanceDistributionData"
      :dl-feed-deviation-data="dlFeedDeviationData"
      @update:selected-plot-id="$emit('update:selectedPlotId', $event)"
      @update:selected-material-balance-plot-id="$emit('update:selectedMaterialBalancePlotId', $event)"
    />

    <!-- Distribution plots (CCE tabs only; DL embeds plots inside DLTestView) -->
    <div
      v-if="activeTestTab === 'oilCce' || activeTestTab === 'gasCce'"
      class="qaqc-overview-distribution-section"
    >
      <DistributionPlotsSection
        :plot-options="distributionPlotOptions"
        :selected-plot-id="selectedPlotId"
        :distribution-data="distributionData"
        @update:selected-plot-id="$emit('update:selectedPlotId', $event)"
      />
    </div>
  </div>
</template>

<script>
import SummaryCards from "./SummaryCards.vue";
import QCStatusOverviewChart from "./QCStatusOverviewChart.vue";
import QCStatusPanel from "./QCStatusPanel.vue";
import QAQCTestTabs from "./QAQCTestTabs.vue";
import CCETestView from "./CCETestView.vue";
import DLTestView from "./DLTestView.vue";
import DistributionPlotsSection from "./DistributionPlotsSection.vue";

export default {
  name: "UnifiedQAQCDashboard",
  components: {
    SummaryCards,
    QCStatusOverviewChart,
    QCStatusPanel,
    QAQCTestTabs,
    CCETestView,
    DLTestView,
    DistributionPlotsSection,
  },
  props: {
    summaryStats: { type: Object, required: true },
    qcStatusOverview: { type: Object, required: true },
    qcStatusPanel: { type: Object, required: true },
    activeTestTab: { type: String, default: "oilCce" },
    scorecardRows: { type: Array, default: () => [] },
    scorecardSummary: { type: Object, default: () => ({}) },
    validationMatrixRows: { type: Array, default: () => [] },
    validationMatrixTitle: { type: String, default: "" },
    validationMatrixDescription: { type: String, default: "" },
    distributionPlotOptions: { type: Array, default: () => [] },
    selectedPlotId: { type: String, default: "" },
    distributionData: { type: Object, default: () => ({}) },
    sampleIds: { type: Array, default: () => [] },
    selectedSampleIds: { type: Array, default: () => [] },
    consistencyCrossTestRows: { type: Array, default: () => [] },
    consistencyOrderingRows: { type: Array, default: () => [] },
    equilibriumPlotRows: { type: Array, default: () => [] },
    dlMaterialBalancePlotOptions: { type: Array, default: () => [] },
    selectedMaterialBalancePlotId: { type: String, default: "" },
    dlMaterialBalanceDistributionData: { type: Object, default: () => ({}) },
    dlFeedDeviationData: { type: Object, default: () => ({}) },
  },
  emits: [
    "update:activeTestTab",
    "update:selectedPlotId",
    "update:selectedMaterialBalancePlotId",
    "update:selectedSampleIds",
  ],
  computed: {
    qcStatusPanelNormalized() {
      const p = this.qcStatusPanel;
      const c = this.parseStatNum(this.summaryStats?.cceTests);
      const d = this.parseStatNum(this.summaryStats?.dlTests);
      if (c === null || d === null) return p;
      const total = c + d;
      if (total <= 0) return p;
      const fix = (block) => {
        const cnt = this.parseStatNum(block?.count);
        const percent =
          cnt !== null ? `${Math.round((cnt / total) * 100)}%` : block.percent;
        return {
          ...block,
          total: String(total),
          percent,
        };
      };
      return {
        ...p,
        passed: fix(p.passed),
        needsReview: fix(p.needsReview),
        failed: fix(p.failed),
      };
    },
  },
  methods: {
    parseStatNum(v) {
      if (v === undefined || v === null || v === "") return null;
      if (typeof v === "number" && !Number.isNaN(v)) return v;
      const s = String(v).trim();
      if (s === "-") return null;
      const n = Number(s.replace(/[^0-9.-]/g, ""));
      return Number.isNaN(n) ? null : n;
    },
  },
};
</script>
